/**
 * TeamTasks.js - Individual Team Member Task View
 * 
 * IMPORTANT: Batched Task Due Date Handling
 * ==========================================
 * For batched workflow tasks (isBatchedTask: true), always use the STAGE-SPECIFIC due date,
 * not the task's dueDate (which is the publish date).
 * 
 * Use getEffectiveDueDate(task) to get the correct due date:
 * - For batched tasks: Returns workflowStages[currentStageIndex].dueDate
 * - For regular tasks: Returns task.dueDate
 * 
 * This applies to:
 * - Date filters (Today, This Week, Overdue, Specific Date)
 * - Count badges (todayTasksCount, overdueTasksCount, thisWeekTasksCount)
 * - Task sorting
 * - Display of due dates
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, AlertTriangle, CalendarPlus, MessageSquare, ThumbsUp, RotateCcw, Archive, CalendarDays, Filter, Users, ExternalLink, RefreshCw } from 'lucide-react';
import JobWorkCard from '@/components/JobWorkCard';
import JobCardSteps from '@/components/JobCardSteps';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format, isTomorrow, parseISO, startOfDay, startOfWeek, endOfWeek, addWeeks, subWeeks, isWithinInterval } from 'date-fns';
import { istNow, istTodayKey, isThisWeekIST } from '@/utils/istClock';
import { useAuth } from '@/contexts/AuthContext';
import { useTasks } from '@/contexts/TaskContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamTasks = () => {
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const toggleTaskSelection = (taskId) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
  };
  const handleBulkMarkDone = async () => {
    if (!window.confirm(`Mark ${selectedTaskIds.size} selected task(s) as Done?`)) return;
    try {
      await Promise.all([...selectedTaskIds].map(id =>
        axios.put(`${API}/tasks/${id}`, { status: 'Done' })
      ));
      toast.success(`${selectedTaskIds.size} task(s) marked as Done`);
      setSelectedTaskIds(new Set());
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error marking tasks done:', error);
      toast.error('Failed to update some tasks');
    }
  };
  const handleBulkArchive = async () => {
    if (!window.confirm(`Archive ${selectedTaskIds.size} selected task(s) for your view?`)) return;
    try {
      await Promise.all([...selectedTaskIds].map(async (taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;
        const currentArchivedBy = task.archivedByUsers || [];
        if (!currentArchivedBy.includes(memberId)) currentArchivedBy.push(memberId);
        return axios.put(`${API}/tasks/${taskId}`, { archivedByUsers: currentArchivedBy });
      }));
      toast.success(`${selectedTaskIds.size} task(s) archived`);
      setSelectedTaskIds(new Set());
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error bulk archiving:', error);
      toast.error('Failed to archive some tasks');
    }
  };
  const { memberId } = useParams();
  const { user } = useAuth();
  // Global task store: keep member-specific fetchData() but re-sync the whole
  // app after any mutation so other views (Dashboard, RA Sheet, etc.) update.
  const { refreshTasks } = useTasks();
  const [member, setMember] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [requestExtensionOpen, setRequestExtensionOpen] = useState(false);
  const [requestExtensionData, setRequestExtensionData] = useState({ requestedDate: '', reason: '' });
  const [selectedTask, setSelectedTask] = useState(null);
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [specificDate, setSpecificDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const specificMonth = selectedMonth && selectedYear ? `${selectedYear}-${selectedMonth}` : '';
  const [showArchived, setShowArchived] = useState(false);
  const [raciFilter, setRaciFilter] = useState('all'); // all, responsible, accountable, consulted, informed
  const [completionData, setCompletionData] = useState({
    completionNote: '',
    completionLinks: ''
  });
  const [approvalData, setApprovalData] = useState({
    approvalStatus: '',
    approvalComment: '',
    newDueDate: ''
  });
  const [extensionData, setExtensionData] = useState({
    newDueDate: '',
    extensionReason: ''
  });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  // Stage completion for batched workflow tasks
  const [stageCompletionDialogOpen, setStageCompletionDialogOpen] = useState(false);
  const [stageCompletionData, setStageCompletionData] = useState({
    note: '',
    links: ''
  });
  // Published stage actions
  const [publishedActionMode, setPublishedActionMode] = useState('publish'); // 'publish' or 'revision'
  const [revisionData, setRevisionData] = useState({
    newDueDate: '',
    revisionNote: '',
    resetToStageIndex: 0,
    assigneeOverrides: {}
  });
  // Workflow editing
  const [workflowEditDialogOpen, setWorkflowEditDialogOpen] = useState(false);
  const [workflowEditData, setWorkflowEditData] = useState([]);

  useEffect(() => {
    if (memberId) {
      fetchData();
    }
    
    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      if (memberId) {
        fetchData();
      }
    }, 30000);
    
    return () => clearInterval(refreshInterval);
    // Note: fetchData intentionally excluded to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const fetchData = async () => {
    try {
      const [memberRes, tasksRes, teamRes] = await Promise.all([
        axios.get(`${API}/team-members/${memberId}`),
        axios.get(`${API}/tasks?memberId=${memberId}&include_archived=true`),
        axios.get(`${API}/team-members`)
      ]);
      setMember(memberRes.data);
      setTasks(tasksRes.data);
      setTeamMembers(teamRes.data);
      
      // Item 6: tasks this member is Accountable, Consulted or Informed on, Done but not yet approved
      // (also work resubmitted after a revision request)
      // Exclude archived tasks (both globally archived and archived by this user)
      const awaitingApproval = tasksRes.data.filter(t => 
        t.status === 'Done' &&
        t.approvalStatus !== 'approved' &&
        (t.accountable?.includes(memberId) || t.consulted?.includes(memberId) || t.informed?.includes(memberId)) &&
        !t.archivedGlobally &&
        !t.archivedByUsers?.includes(memberId)
      );
      setPendingApprovals(awaitingApproval);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load team member tasks');
      setLoading(false);
    }
  };

  // Helper to get member name by ID
  const getMemberName = (id) => {
    const member = teamMembers.find(m => m.id === id);
    return member ? member.name : 'Unknown';
  };

  // Helper to get multiple member names
  const getMemberNames = (ids) => {
    if (!ids || ids.length === 0) return [];
    return ids.map(id => getMemberName(id));
  };

  // Helper to check if member is the current assignee for a batched workflow task
  const isCurrentStageAssignee = (task, checkMemberId) => {
    if (!task.isBatchedTask || !task.workflowStages) return false;
    const currentIdx = task.currentStageIndex || 0;
    if (currentIdx >= task.workflowStages.length) return false;
    return task.workflowStages[currentIdx]?.assigneeId === checkMemberId;
  };

  // Helper to check if member is effectively "responsible" (either in RACI or current stage assignee)
  const isEffectivelyResponsible = (task, checkMemberId) => {
    // For regular tasks, check the responsible array
    if (task.responsible?.includes(checkMemberId)) return true;
    // For batched workflow tasks, check if they're the current stage assignee
    if (isCurrentStageAssignee(task, checkMemberId)) return true;
    return false;
  };

  const handleStatusUpdate = async (taskId, newStatus, onHoldReason = '', completionInfo = null) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'On Hold' && onHoldReason) {
        updateData.onHoldReason = onHoldReason;
      }
      if (newStatus === 'Done' && completionInfo) {
        updateData.completionNote = completionInfo.completionNote;
        updateData.completionLinks = completionInfo.completionLinks ? 
          completionInfo.completionLinks.split(',').map(l => l.trim()).filter(l => l) : [];
      }
      await axios.put(`${API}/tasks/${taskId}`, updateData);
      toast.success('Status updated', {
        description: 'Synced to RA Sheet and Account view'
      });
      fetchData();
      refreshTasks();
      // Trigger dashboard-wide refresh
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleStatusChange = (task, newStatus) => {
    if (newStatus === 'On Hold') {
      const reason = prompt('Please enter the reason for putting this task on hold:');
      if (reason) {
        handleStatusUpdate(task.id, newStatus, reason);
      }
    } else if (newStatus === 'Done') {
      // Open completion dialog for mandatory info
      setSelectedTask(task);
      setCompletionData({ completionNote: '', completionLinks: '' });
      setCompletionDialogOpen(true);
    } else {
      handleStatusUpdate(task.id, newStatus);
    }
  };

  const handleCompletionSubmit = () => {
    if (member?.username !== 'aditya' && !completionData.completionNote && !completionData.completionLinks) {
      toast.error('Please provide either a description or link for the completed work');
      return;
    }
    handleStatusUpdate(selectedTask.id, 'Done', '', completionData);
    setCompletionDialogOpen(false);
    setSelectedTask(null);
    setCompletionData({ completionNote: '', completionLinks: '' });
  };

  // Stage completion handler for batched workflow tasks
  const handleStageComplete = async () => {
    if (!stageCompletionData.note && !stageCompletionData.links) {
      toast.error('Please provide a note or link for your completed work');
      return;
    }
    
    try {
      const links = stageCompletionData.links 
        ? stageCompletionData.links.split(',').map(l => l.trim()).filter(l => l)
        : [];
      
      await axios.post(`${API}/tasks/${selectedTask.id}/complete-stage`, {
        note: stageCompletionData.note,
        links: links
      });
      
      toast.success('Stage completed! Task moved to next person.');
      setStageCompletionDialogOpen(false);
      setSelectedTask(null);
      setStageCompletionData({ note: '', links: '' });
      fetchData();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error completing stage:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete stage');
    }
  };

  const openStageCompletionDialog = (task) => {
    setSelectedTask(task);
    setStageCompletionData({ note: '', links: '' });
    setPublishedActionMode('publish');
    setRevisionData({ newDueDate: '', revisionNote: '' });
    setStageCompletionDialogOpen(true);
  };

  // Handle marking task as published (final completion)
  const handleMarkPublished = async () => {
    try {
      await axios.post(`${API}/tasks/${selectedTask.id}/mark-published`);
      toast.success('Task marked as Published!');
      setStageCompletionDialogOpen(false);
      setSelectedTask(null);
      fetchData();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error marking published:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as published');
    }
  };

  // Handle client revision request - resets workflow with new due date
  const handleClientRevision = async () => {
    if (!revisionData.newDueDate) {
      toast.error('Please select a new due date');
      return;
    }
    
    try {
      await axios.post(`${API}/tasks/${selectedTask.id}/admin-revision`, {
        newDueDate: revisionData.newDueDate,
        revisionNote: revisionData.revisionNote,
        resetToStageIndex: revisionData.resetToStageIndex,
        assigneeOverrides: revisionData.assigneeOverrides
      });
      toast.success('Revision requested! Workflow reset with new dates.');
      setStageCompletionDialogOpen(false);
      setSelectedTask(null);
      setRevisionData({ newDueDate: '', revisionNote: '', resetToStageIndex: 0, assigneeOverrides: {} });
      fetchData();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error requesting revision:', error);
      toast.error(error.response?.data?.detail || 'Failed to request revision');
    }
  };

  // Open workflow edit dialog
  const openWorkflowEditDialog = (task) => {
    setSelectedTask(task);
    setWorkflowEditData(task.workflowStages?.map(s => ({
      name: s.name,
      assigneeId: s.assigneeId,
      dueDate: s.dueDate
    })) || []);
    setWorkflowEditDialogOpen(true);
  };

  // Save workflow changes
  const handleSaveWorkflow = async () => {
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}/workflow-stages`, {
        stages: workflowEditData
      });
      toast.success('Workflow updated successfully!');
      setWorkflowEditDialogOpen(false);
      setSelectedTask(null);
      fetchData();
      refreshTasks();
      window.dispatchEvent(new Event('refreshDashboard'));
    } catch (error) {
      console.error('Error updating workflow:', error);
      toast.error(error.response?.data?.detail || 'Failed to update workflow');
    }
  };

  // Approval handlers (for Accountable people)
  const openApprovalDialog = (task) => {
    setSelectedTask(task);
    setApprovalData({ approvalStatus: '', approvalComment: '', newDueDate: '' });
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    if (!approvalData.approvalStatus) {
      toast.error('Please select Approve or Request Revision');
      return;
    }
    if (approvalData.approvalStatus === 'revision_requested' && !approvalData.approvalComment) {
      toast.error('Please provide feedback for the revision request');
      return;
    }

    try {
      const updateData = {
        approvalStatus: approvalData.approvalStatus,
        approvalComment: approvalData.approvalComment,
        approvalBy: user?.id,
        approvalDate: new Date().toISOString()
      };
      
      if (approvalData.approvalStatus === 'revision_requested') {
        updateData.status = 'In Progress'; // Send back to in progress
        if (approvalData.newDueDate) {
          updateData.dueDate = approvalData.newDueDate;
        }
      }

      await axios.put(`${API}/tasks/${selectedTask.id}`, updateData);
      toast.success(approvalData.approvalStatus === 'approved' ? 'Task approved!' : 'Revision requested');
      setApprovalDialogOpen(false);
      setSelectedTask(null);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error('Failed to update approval status');
    }
  };

  // Extension handlers (for overdue tasks - Admin or Accountable)
  const openExtensionDialog = (task) => {
    setSelectedTask(task);
    setExtensionData({ newDueDate: '', extensionReason: '' });
    setExtensionDialogOpen(true);
  };
  const openRequestExtensionDialog = (task) => {
    setSelectedTask(task);
    setRequestExtensionData({ requestedDate: '', reason: '' });
    setRequestExtensionOpen(true);
  };
  const handleRequestExtensionSubmit = async () => {
    if (!requestExtensionData.requestedDate) {
      toast.error('Please select a proposed date');
      return;
    }
    if (!requestExtensionData.reason) {
      toast.error('Please provide a reason for the request');
      return;
    }
    try {
      await axios.post(`${API}/tasks/${selectedTask.id}/request-extension`, {
        requestedDate: requestExtensionData.requestedDate,
        reason: requestExtensionData.reason
      });
      toast.success('Extension request submitted for admin review');
      setRequestExtensionOpen(false);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error submitting extension request:', error);
      toast.error('Failed to submit extension request');
    }
  };

  const handleExtensionSubmit = async () => {
    if (!extensionData.newDueDate) {
      toast.error('Please select a new due date');
      return;
    }
    if (!extensionData.extensionReason) {
      toast.error('Please provide a reason for the extension');
      return;
    }

    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        originalDueDate: selectedTask.originalDueDate || selectedTask.dueDate,
        dueDate: extensionData.newDueDate,
        extensionReason: extensionData.extensionReason,
        extendedBy: user?.id
      });
      toast.success('Due date extended');
      setExtensionDialogOpen(false);
      setSelectedTask(null);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error extending due date:', error);
      toast.error('Failed to extend due date');
    }
  };

  // Check if current user can approve (is Accountable for the task)
  const canApprove = (task) => {
    // User must be Accountable for this task, task must be Done, and not already approved
    const currentUserId = user?.id;
    return task.accountable?.includes(currentUserId) && task.status === 'Done' && task.approvalStatus !== 'approved';
  };

  // Check if current user can extend (is Admin or Accountable) and task is overdue
  const canExtend = (task) => {
    const currentUserId = user?.id;
    const isAdminOrAccountable = user?.role === 'admin' || task.accountable?.includes(currentUserId);
    return isAdminOrAccountable && task.isLagging;
  };

  // Archive a completed task (USER-SPECIFIC - only hides for this user)
  const handleArchiveAllDone = async () => {
    if (!window.confirm('Archive all of your Done tasks?')) return;
    try {
      const res = await axios.post(`${API}/tasks/bulk-archive-done-for-member?memberId=${memberId}`);
      toast.success(res.data.message);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error archiving done tasks:', error);
      toast.error('Failed to archive done tasks');
    }
  };
  const handleArchive = async (task) => {
    try {
      // Get current archivedByUsers array or initialize empty
      const currentArchivedBy = task.archivedByUsers || [];
      // Add current user if not already in list
      if (!currentArchivedBy.includes(memberId)) {
        currentArchivedBy.push(memberId);
      }
      
      await axios.put(`${API}/tasks/${task.id}`, {
        archivedByUsers: currentArchivedBy
      });
      toast.success('Task archived for your view');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  // Unarchive a task (USER-SPECIFIC)
  const handleUnarchive = async (task) => {
    try {
      // Remove current user from archivedByUsers array
      const currentArchivedBy = task.archivedByUsers || [];
      const updatedArchivedBy = currentArchivedBy.filter(id => id !== memberId);
      
      await axios.put(`${API}/tasks/${task.id}`, {
        archivedByUsers: updatedArchivedBy
      });
      toast.success('Task restored for your view');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  // Check if task is archived for current user
  const isArchivedForUser = (task) => {
    // Global archive (by admin from Client Deliverables) hides from everyone
    if (task.archivedGlobally) return true;
    // User-specific archive
    return task.archivedByUsers?.includes(memberId);
  };

  // Filter tasks by date
  const filterTasksByDate = (taskList) => {
    if (dateFilter === 'all' || dateFilter === 'done') return taskList;
    
    return taskList.filter(task => {
      // For batched tasks, use the current stage's due date
      let effectiveDueDate = task.dueDate;
      if (task.isBatchedTask && task.workflowStages) {
        const currentStage = task.workflowStages[task.currentStageIndex || 0];
        if (currentStage?.dueDate) {
          effectiveDueDate = currentStage.dueDate;
        }
      }
      
      if (!effectiveDueDate) return dateFilter === 'no-date';
      
      try {
        const dueDate = parseISO(effectiveDueDate);
        const now = istNow();
        
        switch (dateFilter) {
          case 'today': {
            // CR-15 (updated): Start Date is today (IST); a task without a Start Date uses its Due Date
            if (['Done', 'done'].includes(task.status)) return false;
            return format(task.startDate ? parseISO(task.startDate) : dueDate, 'yyyy-MM-dd') === istTodayKey();
          }
          case 'tomorrow':
            return isTomorrow(dueDate);
          case 'this-week':
            return isThisWeekIST(dueDate, { weekStartsOn: 1 }); // Week starts on Monday
          case 'last-week': {
            const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
          }
          case 'next-week': {
            const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
            return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
          }
          case 'overdue':
            // For batched tasks, check if current stage due date is overdue
            const todayStart = new Date(); todayStart.setHours(0,0,0,0); return dueDate < todayStart && task.status?.toLowerCase() !== 'done';
          case 'specific': {
            if (!specificDate) return true;
            const pickedDay = parseISO(specificDate);
            const effectiveStartSpecific = task.startDate || task.assignedDate;
            if (effectiveStartSpecific) {
              const startDay = new Date(parseISO(effectiveStartSpecific)); startDay.setHours(0,0,0,0);
              const dueDay = new Date(dueDate); dueDay.setHours(0,0,0,0);
              return startDay <= pickedDay && pickedDay <= dueDay;
            }
            return format(dueDate, 'yyyy-MM-dd') === specificDate;
          }
          case 'specific-month':
            if (!specificMonth) return true;
            return format(dueDate, 'yyyy-MM') === specificMonth;
          case 'no-date':
            return false;
          default:
            return true;
        }
      } catch {
        return false;
      }
    });
  };

  // Get filtered tasks (by date, archive status, and RACI role)
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    // Filter by archive status (using user-specific archive logic)
    if (!showArchived) {
      filtered = filtered.filter(t => !isArchivedForUser(t));
      if (dateFilter === 'done') {
        filtered = filtered.filter(t => ['Done', 'done'].includes(t.status));
      } else {
        filtered = filtered.filter(t => !['Done', 'done'].includes(t.status));
      }
    } else {
      filtered = filtered.filter(t => isArchivedForUser(t));
    }
    
    // Filter by RACI role (for the current team member)
    if (raciFilter !== 'all') {
      filtered = filtered.filter(task => {
        switch (raciFilter) {
          case 'responsible':
            // Include both RACI responsible AND batched task current stage assignees
            return isEffectivelyResponsible(task, memberId);
          case 'accountable':
            return task.accountable?.includes(memberId);
          case 'consulted':
            return task.consulted?.includes(memberId);
          case 'informed':
            return task.informed?.includes(memberId);
          default:
            return true;
        }
      });
    }
    
    // Filter by date
    filtered = filterTasksByDate(filtered);
    
    return filtered;
  };

  // Helper to filter tasks by RACI role
  const filterByRaci = (taskList) => {
    if (raciFilter === 'all') return taskList;
    return taskList.filter(task => {
      switch (raciFilter) {
        case 'responsible':
          // Include both RACI responsible AND batched task current stage assignees
          return isEffectivelyResponsible(task, memberId);
        case 'accountable':
          return task.accountable?.includes(memberId);
        case 'consulted':
          return task.consulted?.includes(memberId);
        case 'informed':
          return task.informed?.includes(memberId);
        default:
          return true;
      }
    });
  };

  // Helper to get effective due date (member's stage-specific for batched tasks)
  const getEffectiveDueDate = (task) => {
    // If memberStageInfo is present (from backend), use that member's stage due date
    if (task.memberStageInfo?.stageDueDate) {
      return task.memberStageInfo.stageDueDate;
    }
    // Fallback: for batched tasks, use current stage due date
    if (task.isBatchedTask && task.workflowStages) {
      const currentStage = task.workflowStages[task.currentStageIndex || 0];
      if (currentStage?.dueDate) {
        return currentStage.dueDate;
      }
    }
    return task.dueDate;
  };

  // Helper to check if it's this member's turn
  const isMyTurn = (task) => {
    if (task.memberStageInfo) {
      return task.memberStageInfo.isCurrentTurn;
    }
    // Fallback for non-batched tasks or when memberStageInfo not present
    return isCurrentStageAssignee(task, memberId);
  };

  // Count stats (respecting RACI filter and user-specific archive)
  const raciFilteredTasks = filterByRaci(tasks);
  const activeTasksCount = raciFilteredTasks.filter(t => !isArchivedForUser(t) && !['Done', 'done'].includes(t.status)).length;
  const doneTasksCount = raciFilteredTasks.filter(t => !isArchivedForUser(t) && ['Done', 'done'].includes(t.status)).length;
  const archivedTasksCount = raciFilteredTasks.filter(t => isArchivedForUser(t)).length;
  const todayTasksCount = raciFilteredTasks.filter(t => {
    const effectiveDate = getEffectiveDueDate(t);
    if (!effectiveDate || isArchivedForUser(t) || ['Done', 'done'].includes(t.status)) return false;
    try {
      const due = parseISO(effectiveDate);
      return format(t.startDate ? parseISO(t.startDate) : due, 'yyyy-MM-dd') === istTodayKey(); // CR-15 (updated): start date today (IST), else due today
    } catch {
      return false;
    }
  }).length;
  const overdueTasksCount = raciFilteredTasks.filter(t => {
    if (isArchivedForUser(t)) return false;
    const effectiveDate = getEffectiveDueDate(t);
    if (!effectiveDate) return false;
    try {
      const todayStart = new Date(); todayStart.setHours(0,0,0,0); return parseISO(effectiveDate) < todayStart && t.status?.toLowerCase() !== 'done';
    } catch {
      return false;
    }
  }).length;
  const thisWeekTasksCount = raciFilteredTasks.filter(t => {
    const effectiveDate = getEffectiveDueDate(t);
    if (!effectiveDate || isArchivedForUser(t)) return false;
    try {
      return isThisWeekIST(parseISO(effectiveDate), { weekStartsOn: 1 });
    } catch {
      return false;
    }
  }).length;
  const lastWeekTasksCount = raciFilteredTasks.filter(t => {
    const effectiveDate = getEffectiveDueDate(t);
    if (!effectiveDate || isArchivedForUser(t)) return false;
    try {
      const dueDate = parseISO(effectiveDate);
      const now = istNow();
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: lastWeekStart, end: lastWeekEnd });
    } catch {
      return false;
    }
  }).length;
  const nextWeekTasksCount = raciFilteredTasks.filter(t => {
    const effectiveDate = getEffectiveDueDate(t);
    if (!effectiveDate || isArchivedForUser(t)) return false;
    try {
      const dueDate = parseISO(effectiveDate);
      const now = istNow();
      const nextWeekStart = startOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      const nextWeekEnd = endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 });
      return isWithinInterval(dueDate, { start: nextWeekStart, end: nextWeekEnd });
    } catch {
      return false;
    }
  }).length;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'ongoing':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
    }
  };

  const groupTasksByAccount = () => {
    // Get filtered tasks first
    const filteredTasks = getFilteredTasks();
    
    // Sort: today first, then each later date in order; past due dates after that; no due date last.
    // Within the same date: priority (High, Medium, Low), then title A-Z.
    const sortTodayKey = istTodayKey();
    const sortDayKey = (d) => { try { return d ? format(parseISO(d), 'yyyy-MM-dd') : ''; } catch { return ''; } };
    const sortGroup = (k) => (!k ? 2 : k >= sortTodayKey ? 0 : 1);
    const sortPriority = { high: 0, med: 1, medium: 1, low: 2 };
    const sortedTasks = [...filteredTasks].sort((a, b) => {
      const ak = sortDayKey(getEffectiveDueDate(a)), bk = sortDayKey(getEffectiveDueDate(b));
      const g = sortGroup(ak) - sortGroup(bk);
      if (g !== 0) return g;
      if (ak !== bk) return ak < bk ? -1 : 1;
      const p = (sortPriority[a.priority] ?? 3) - (sortPriority[b.priority] ?? 3);
      if (p !== 0) return p;
      return (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' });
    });
    
    return sortedTasks.reduce((acc, task) => {
      const account = task.accountName || 'Unassigned';
      if (!acc[account]) acc[account] = [];
      acc[account].push(task);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const groupedTasks = groupTasksByAccount();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="team-tasks-page"
    >
      {/* Member Header */}
      <div className="flex items-center gap-4 sm:gap-6">
        <div className="h-14 w-14 sm:h-20 sm:w-20 flex-shrink-0 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center text-xl sm:text-3xl font-bold">
          {member?.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-4xl font-bold tracking-tight truncate">{member?.name}</h1>
          <div className="flex items-center gap-2 sm:gap-4 mt-2 flex-wrap">
            <span className="text-sm text-muted-foreground">
              {activeTasksCount} total tasks
            </span>
            <span className="text-sm text-muted-foreground">•</span>
            <span className="text-sm text-success">
              {tasks.filter(t => t.status === 'Done').length} completed
            </span>
            {member?.laggingTaskCount > 0 && (
              <>
                <span className="text-sm text-muted-foreground">•</span>
                <span className="text-sm text-error flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  {member.laggingTaskCount} overdue
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pending Approvals Alert - Show if member is accountable for tasks awaiting approval */}
      {pendingApprovals.length > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <MessageSquare className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-800">
                {pendingApprovals.length} Task{pendingApprovals.length > 1 ? 's' : ''} Submitted for Review
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Open the link to see the work. Only the Accountable person approves or asks for a revision.
              </p>
              <div className="mt-3 space-y-2">
                {pendingApprovals.slice(0, 5).map(task => (
                  <div 
                    key={task.id} 
                    className="p-3 bg-white rounded-lg border border-amber-200"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {task.accountName} • Completed by: {task.responsible?.map(id => getMemberName(id)).join(', ') || 'Unknown'} • You are: {task.accountable?.includes(memberId) ? 'Accountable' : task.consulted?.includes(memberId) ? 'Consulted' : 'Informed'}
                        </p>
                        {/* Completion Note */}
                        {task.completionNote && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                            <span className="font-medium">Note:</span> {task.completionNote}
                          </p>
                        )}
                        {/* Completion Links */}
                        {task.completionLinks && task.completionLinks.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {task.completionLinks.map((link, idx) => (
                              <a 
                                key={idx}
                                href={link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-3 w-3" />
                                View Link {idx + 1}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      {task.accountable?.includes(memberId) && (
                        <Button
                          size="sm"
                          onClick={() => openApprovalDialog(task)}
                          className="flex-shrink-0 bg-amber-600 hover:bg-amber-700"
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {pendingApprovals.length > 5 && (
                  <p className="text-xs text-amber-600 text-center">
                    And {pendingApprovals.length - 5} more tasks...
                  </p>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Date Filter and Archive Controls */}
      <Card className="p-4 bg-white border-border/60">
        <div className="flex flex-wrap items-center gap-4">
          {/* Date Filter Buttons */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Filter:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('all'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-all-btn"
            >
              All ({activeTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('today'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-today-btn"
            >
              Today ({todayTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'last-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('last-week'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-last-week-btn"
            >
              Last Week ({lastWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'this-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('this-week'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'this-week' ? 'bg-blue-600 hover:bg-blue-700' : 'text-blue-600 border-blue-300'}
              data-testid="filter-this-week-btn"
            >
              This Week ({thisWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'next-week' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('next-week'); setSpecificDate(''); setShowArchived(false); }}
              data-testid="filter-next-week-btn"
            >
              Next Week ({nextWeekTasksCount})
            </Button>
            <Button
              size="sm"
              variant={dateFilter === 'overdue' ? 'default' : 'outline'}
              onClick={() => { setDateFilter('overdue'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'overdue' ? 'bg-red-600 hover:bg-red-700' : 'text-red-600 border-red-300'}
              data-testid="filter-overdue-btn"
            >
              <AlertTriangle className="h-3 w-3 mr-1" />
              Overdue ({overdueTasksCount})
            </Button>
          </div>

          {/* Date Picker */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Input
              type="date"
              value={specificDate}
              onChange={(e) => {
                setSpecificDate(e.target.value);
                setDateFilter('specific');
              }}
              className="w-40 h-9 bg-white"
              data-testid="date-picker"
            />
            {specificDate && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSpecificDate(''); setDateFilter('all'); setShowArchived(false); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Month Picker */}
          <div className="flex items-center gap-2">
            <Select
              value={selectedMonth}
              onValueChange={(v) => { setSelectedMonth(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-28 h-9 bg-white" data-testid="month-picker">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map((m, idx) => (
                  <SelectItem key={m} value={m}>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'][idx]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={selectedYear}
              onValueChange={(v) => { setSelectedYear(v); setDateFilter('specific-month'); }}
            >
              <SelectTrigger className="w-24 h-9 bg-white" data-testid="year-picker">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {[2025, 2026, 2027].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(selectedMonth || selectedYear) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setSelectedMonth(''); setSelectedYear(''); setDateFilter('all'); setShowArchived(false); }}
                className="h-9 px-2"
              >
                Clear
              </Button>
            )}
          </div>
          {/* RACI Role Filter */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Select value={raciFilter} onValueChange={setRaciFilter}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="raci-filter">
                <SelectValue placeholder="RACI Role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="responsible">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-xs font-bold">R</span>
                    Responsible
                  </span>
                </SelectItem>
                <SelectItem value="accountable">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-purple-500 text-white rounded text-xs font-bold">A</span>
                    Accountable
                  </span>
                </SelectItem>
                <SelectItem value="consulted">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-green-500 text-white rounded text-xs font-bold">C</span>
                    Consulted
                  </span>
                </SelectItem>
                <SelectItem value="informed">
                  <span className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-gray-500 text-white rounded text-xs font-bold">I</span>
                    Informed
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Archive Toggle */}
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {user?.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
                onClick={handleArchiveAllDone}
                data-testid="archive-all-done-btn"
              >
                <Archive className="h-4 w-4 mr-1" />
                Archive Done Tasks
              </Button>
            )}
            <Button
              size="sm"
              variant={dateFilter === 'done' && !showArchived ? 'default' : 'outline'}
              onClick={() => { setDateFilter('done'); setSpecificDate(''); setShowArchived(false); }}
              className={dateFilter === 'done' && !showArchived ? 'bg-green-600 hover:bg-green-700' : 'text-green-700 border-green-300'}
              data-testid="filter-done-btn"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Done ({doneTasksCount})
            </Button>
            <Button
              size="sm"
              variant={showArchived ? 'default' : 'outline'}
              onClick={() => { setShowArchived(!showArchived); setDateFilter('all'); setSpecificDate(''); }}
              className={showArchived ? 'bg-slate-700' : ''}
              data-testid="toggle-archived-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              {showArchived ? 'Showing Archived' : 'Show Archived'} ({archivedTasksCount})
            </Button>
          </div>
        </div>
      </Card>

      {/* Bulk Action Bar */}
      {selectedTaskIds.size > 0 && (
        <Card className="p-4 bg-blue-50 border-blue-200 flex items-center justify-between mb-4">
          <span className="text-sm font-medium">{selectedTaskIds.size} task(s) selected</span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-green-400 text-green-700"
              onClick={handleBulkMarkDone}
              data-testid="bulk-mark-done-btn"
            >
              Mark Selected as Done
            </Button>
            {user?.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                className="border-slate-400 text-slate-700"
                onClick={handleBulkArchive}
                data-testid="bulk-archive-btn"
              >
                Archive Selected
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedTaskIds(new Set())}
            >
              Clear
            </Button>
          </div>
        </Card>
      )}
      {/* Tasks by Account */}
      {Object.entries(groupedTasks).map(([account, accountTasks]) => (
        <div key={account} data-testid={`account-section-${account.toLowerCase().replace(/\s+/g, '-')}`}>
          <h2 className="text-2xl font-semibold mb-4">{account}</h2>
          <div className="grid gap-4">
            {accountTasks.map((task) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                data-testid={`task-card-${task.id}`}
              >
                <Card className={`border hover:shadow-md transition-shadow bg-white ${
                  task.isLagging 
                    ? 'border-l-4 border-l-red-500 bg-red-50' 
                    : task.priority === 'high'
                    ? 'border-l-4 border-l-orange-400 bg-orange-50'
                    : task.status === 'Done'
                    ? 'border-l-4 border-l-green-500 bg-green-50'
                    : 'border-l-4 border-l-blue-400'
                }`}>
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <input
                        type="checkbox"
                        className="mt-2"
                        checked={selectedTaskIds.has(task.id)}
                        onChange={() => toggleTaskSelection(task.id)}
                        data-testid={`select-task-${task.id}`}
                      />
                      <div className="mt-1">
                        {getStatusIcon(task.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <h3 className="text-lg font-semibold">{task.title}</h3>
                              {/* Your RACI Role Badge - includes batched workflow current stage assignee */}
                              {task.isBatchedTask && task.memberStageInfo ? (
                                // For batched tasks, show member's stage info
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  task.memberStageInfo.isCurrentTurn 
                                    ? 'bg-blue-500 text-white animate-pulse' 
                                    : 'bg-gray-300 text-gray-700'
                                }`}>
                                  {task.memberStageInfo.isCurrentTurn ? 'Your Turn' : `Waiting (${task.memberStageInfo.stageName})`}
                                </span>
                              ) : isEffectivelyResponsible(task, memberId) && (
                                <span className="px-2 py-0.5 bg-blue-500 text-white rounded text-xs font-bold">
                                  {isCurrentStageAssignee(task, memberId) ? 'Your Turn' : 'Your Role: R'}
                                </span>
                              )}
                              {task.accountable?.includes(memberId) && (
                                <span className="px-2 py-0.5 bg-purple-500 text-white rounded text-xs font-bold">
                                  Your Role: A
                                </span>
                              )}
                              {task.consulted?.includes(memberId) && (
                                <span className="px-2 py-0.5 bg-green-500 text-white rounded text-xs font-bold">
                                  Your Role: C
                                </span>
                              )}
                              {task.informed?.includes(memberId) && (
                                <span className="px-2 py-0.5 bg-gray-500 text-white rounded text-xs font-bold">
                                  Your Role: I
                                </span>
                              )}
                            </div>
                            {task.deliverable && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Division: {task.deliverable}
                              </p>
                            )}
                            <JobWorkCard task={task} className="mt-1" />
                            
                            {/* Full RACI Matrix - Visible to everyone */}
                            {/* For batched tasks, show current stage assignee as responsible */}
                            <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
                              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">
                                {task.isBatchedTask ? 'Current Assignment' : 'RACI Matrix'}
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                {task.isBatchedTask && task.workflowStages ? (
                                  // For batched tasks, show current stage assignee as R
                                  <div className="flex items-start gap-2">
                                    <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-xs font-bold shrink-0">R</span>
                                    <span className="text-slate-700">
                                      {getMemberName(task.workflowStages[task.currentStageIndex || 0]?.assigneeId)}
                                      <span className="text-xs text-slate-500 ml-1">
                                        ({task.workflowStages[task.currentStageIndex || 0]?.name})
                                      </span>
                                    </span>
                                  </div>
                                ) : (
                                  // Regular tasks show full RACI
                                  task.responsible?.length > 0 && (
                                    <div className="flex items-start gap-2">
                                      <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-xs font-bold shrink-0">R</span>
                                      <span className="text-slate-700">{getMemberNames(task.responsible).join(', ')}</span>
                                    </div>
                                  )
                                )}
                                {task.accountable?.length > 0 && (
                                  <div className="flex items-start gap-2">
                                    <span className="px-1.5 py-0.5 bg-purple-500 text-white rounded text-xs font-bold shrink-0">A</span>
                                    <span className="text-slate-700">{getMemberNames(task.accountable).join(', ')}</span>
                                  </div>
                                )}
                                {!task.isBatchedTask && task.consulted?.length > 0 && (
                                  <div className="flex items-start gap-2">
                                    <span className="px-1.5 py-0.5 bg-green-500 text-white rounded text-xs font-bold shrink-0">C</span>
                                    <span className="text-slate-700">{getMemberNames(task.consulted).join(', ')}</span>
                                  </div>
                                )}
                                {!task.isBatchedTask && task.informed?.length > 0 && (
                                  <div className="flex items-start gap-2">
                                    <span className="px-1.5 py-0.5 bg-gray-500 text-white rounded text-xs font-bold shrink-0">I</span>
                                    <span className="text-slate-700">{getMemberNames(task.informed).join(', ')}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Workflow Stages for Batched Tasks */}
                            {task.isBatchedTask && task.workflowStages && (
                              <div className="mt-3 p-3 bg-gradient-to-r from-violet-50 to-blue-50 rounded-lg border border-violet-200">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-semibold text-violet-700 uppercase">Workflow Progress</p>
                                  {task.postCount && (
                                    <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded">
                                      {task.postCount} posts
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {task.workflowStages.map((stage, idx) => (
                                    <div key={idx} className="flex items-center">
                                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                                        stage.status === 'done' ? 'bg-green-200 text-green-800' :
                                        stage.status === 'in_progress' ? 'bg-blue-200 text-blue-800 animate-pulse' :
                                        'bg-gray-200 text-gray-600'
                                      }`}>
                                        {stage.name}
                                        <span className="ml-1 opacity-70">
                                          ({getMemberName(stage.assigneeId)?.split(' ')[0]})
                                        </span>
                                        {stage.dueDate && (
                                          <span className="ml-1 text-[10px] opacity-60">
                                            • {format(new Date(stage.dueDate), 'MMM d')}
                                          </span>
                                        )}
                                      </div>
                                      {idx < task.workflowStages.length - 1 && (
                                        <span className="text-gray-400 mx-1">→</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {/* Show ideation reference if available */}
                                {task.ideationReference && task.currentStageIndex > 0 && (
                                  <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                                    <p className="text-xs font-medium text-amber-700">📋 Ideation/Content Reference:</p>
                                    {task.ideationReference.note && (
                                      <p className="text-xs text-gray-600 mt-1">{task.ideationReference.note}</p>
                                    )}
                                    {task.ideationReference.links?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {task.ideationReference.links.map((link, linkIdx) => (
                                          <a
                                            key={linkIdx}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                          >
                                            <ExternalLink className="h-3 w-3" />
                                            {link.includes('drive.google') ? 'Google Drive' : 
                                             link.includes('docs.google') ? 'Google Doc' : `Link ${linkIdx + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                                {/* Show deliverables from previous stages */}
                                {task.workflowStages.filter(s => s.status === 'done' && s.deliverables).map((stage, idx) => (
                                  <div key={idx} className="mt-2 p-2 bg-white rounded border border-green-200">
                                    <p className="text-xs font-medium text-green-700">{stage.name} deliverables:</p>
                                    {stage.deliverables.note && (
                                      <p className="text-xs text-gray-600 mt-1">{stage.deliverables.note}</p>
                                    )}
                                    {stage.deliverables.links?.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {stage.deliverables.links.map((link, linkIdx) => (
                                          <a
                                            key={linkIdx}
                                            href={link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                                          >
                                            <ExternalLink className="h-3 w-3" />
                                            {link.includes('drive.google') ? 'Google Drive' : `Link ${linkIdx + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* For batched workflow tasks - show Complete Stage button only when it's their turn */}
                            {task.isBatchedTask && task.memberStageInfo?.isCurrentTurn && task.status?.toLowerCase() !== 'done' && (
                              <Button
                                size="sm"
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                                onClick={() => openStageCompletionDialog(task)}
                                data-testid={`complete-stage-${task.id}`}
                              >
                                Complete Stage
                              </Button>
                            )}
                            {/* Regular status dropdown for non-batched tasks */}
                            {!task.isBatchedTask && task.responsible?.includes(memberId) ? (
                              <Select
                                value={task.status}
                                onValueChange={(value) => handleStatusChange(task, value)}
                              >
                                <SelectTrigger className="w-32" data-testid={`status-select-${task.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                  <SelectItem value="Yet to Start">Yet to Start</SelectItem>
                                  <SelectItem value="In Progress">In Progress</SelectItem>
                                  <SelectItem value="Done">Done</SelectItem>
                                  <SelectItem value="On Hold">On Hold</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : !task.isBatchedTask && (
                              <span className={`px-3 py-1.5 rounded text-sm font-medium ${
                                task.status === 'Done' ? 'bg-green-100 text-green-700' :
                                task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                                task.status === 'On Hold' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {task.status}
                              </span>
                            )}
                            {/* Status badge for batched tasks */}
                            {task.isBatchedTask && (
                              <span className={`px-3 py-1.5 rounded text-sm font-medium ${
                                task.status === 'done' ? 'bg-green-100 text-green-700' :
                                task.memberStageInfo?.isCurrentTurn ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {task.memberStageInfo?.isCurrentTurn ? 'Your Turn' : 
                                 task.status === 'done' ? 'Done' : 'Waiting'}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 mt-4 text-sm">
                          {task.taskType && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                              {task.taskType}
                            </span>
                          )}
                          {task.priority && (
                            <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                              task.priority === 'high' ? 'bg-red-100 text-red-700' :
                              task.priority === 'med' ? 'bg-orange-100 text-orange-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {task.priority === 'high' ? '🔴' : task.priority === 'med' ? '🟡' : '🟢'} {task.priority.toUpperCase()}
                            </span>
                          )}
                          {/* Show member's stage-specific due date for batched tasks */}
                          {task.isBatchedTask && task.memberStageInfo ? (
                            (() => {
                              const stageDueDate = task.memberStageInfo.stageDueDate;
                              const stageName = task.memberStageInfo.stageName;
                              const isOverdue = stageDueDate && new Date(stageDueDate) < new Date() && task.memberStageInfo.isCurrentTurn;
                              return stageDueDate ? (
                                <span className={`font-mono text-xs ${
                                  isOverdue ? 'text-red-600 font-bold bg-red-100 px-2 py-1 rounded' : 'text-gray-600'
                                }`}>
                                  {isOverdue && '⚠️ '} Your Due Date ({stageName}): {format(new Date(stageDueDate), 'MMM dd, yyyy')}
                                </span>
                              ) : null;
                            })()
                          ) : task.isBatchedTask && task.workflowStages ? (
                            (() => {
                              const currentStage = task.workflowStages[task.currentStageIndex || 0];
                              const stageDueDate = currentStage?.dueDate;
                              const isOverdue = stageDueDate && new Date(stageDueDate) < new Date();
                              return stageDueDate ? (
                                <span className={`font-mono text-xs ${
                                  isOverdue ? 'text-red-600 font-bold bg-red-100 px-2 py-1 rounded' : 'text-gray-600'
                                }`}>
                                  {isOverdue && '⚠️ '} Your Due Date ({currentStage?.name}): {format(new Date(stageDueDate), 'MMM dd, yyyy')}
                                </span>
                              ) : null;
                            })()
                          ) : (
                            task.dueDate && (
                              <span className={`font-mono text-xs ${
                                task.isLagging ? 'text-red-600 font-bold bg-red-100 px-2 py-1 rounded' : 'text-gray-600'
                              }`}>
                                {task.isLagging && '⚠️ '} Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                              </span>
                            )
                          )}
                        </div>

                        {task.startDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            Start: {format(new Date(task.startDate), 'MMM dd, yyyy')}
                          </p>
                        )}
                        {(task.assignedBy || task.assignedDate) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {task.assignedBy && `Assigned By: ${getMemberName(task.assignedBy)}`}
                            {task.assignedBy && task.assignedDate && ' \u2022 '}
                            {task.assignedDate && `Assigned: ${format(new Date(task.assignedDate), 'MMM dd, yyyy')}`}
                          </p>
                        )}
                        {task.completedDate && (
                          <p className="text-xs text-green-700 font-medium mt-1">
                            Completed: {format(new Date(task.completedDate), 'MMM dd, yyyy')}
                          </p>
                        )}
                        {/* Show publish date for batched tasks */}
                        {task.isBatchedTask && task.dueDate && (
                          <p className="text-xs text-muted-foreground mt-1">
                            📅 Publish Date: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                          </p>
                        )}

                        <JobCardSteps division={task.deliverable} job={task.job} className="mt-3" />
                        {task.instructions && (
                          <p className="text-sm text-gray-600 mt-3 p-2 bg-gray-100 rounded">
                            {task.instructions}
                          </p>
                        )}
                        {task.outcome && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-[10px] font-semibold text-blue-700 uppercase mb-0.5">Outcome</p>
                            <p className="text-sm text-blue-900">{task.outcome}</p>
                          </div>
                        )}

                        {task.onHoldReason && task.status === 'On Hold' && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-[10px] font-semibold text-red-700 mb-0.5 uppercase leading-tight">On Hold Reason:</p>
                            <p className="text-xs text-red-800 leading-snug">{task.onHoldReason}</p>
                          </div>
                        )}

                        {task.links && task.links.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-muted-foreground mb-2">Links:</p>
                            <div className="flex flex-col gap-1">
                              {task.links.map((link, idx) => (
                                <a
                                  key={idx}
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline break-all"
                                >
                                  {link.length > 50 ? link.substring(0, 50) + '...' : link}
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Approval Status Display */}
                        {task.approvalStatus && (
                          <div className={`mt-3 p-3 rounded-lg border ${
                            task.approvalStatus === 'approved' ? 'bg-green-50 border-green-200' :
                            task.approvalStatus === 'revision_requested' ? 'bg-orange-50 border-orange-200' :
                            'bg-yellow-50 border-yellow-200'
                          }`}>
                            <div className="flex items-center gap-2">
                              {task.approvalStatus === 'approved' && <ThumbsUp className="h-4 w-4 text-green-600" />}
                              {task.approvalStatus === 'revision_requested' && <RotateCcw className="h-4 w-4 text-orange-600" />}
                              <span className={`text-sm font-semibold ${
                                task.approvalStatus === 'approved' ? 'text-green-700' : 'text-orange-700'
                              }`}>
                                {task.approvalStatus === 'approved' ? 'Approved' : 'Revision Requested'}
                              </span>
                            </div>
                            {task.approvalComment && (
                              <p className="text-sm mt-2">{task.approvalComment}</p>
                            )}
                          </div>
                        )}

                        {/* Extension Info Display */}
                        {task.extensionReason && (
                          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                            <p className="text-xs font-semibold text-purple-700 mb-1">Due Date Extended</p>
                            <p className="text-sm text-purple-800">{task.extensionReason}</p>
                            {task.originalDueDate && (
                              <p className="text-xs text-purple-600 mt-1">
                                Original due: {format(new Date(task.originalDueDate), 'MMM dd, yyyy')}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-4 pt-3 border-t flex-wrap">
                          {/* Edit Workflow Button - for Admins on batched tasks */}
                          {task.isBatchedTask && task.workflowStages && user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openWorkflowEditDialog(task)}
                              className="text-violet-700 border-violet-300 hover:bg-violet-50"
                              data-testid={`edit-workflow-${task.id}`}
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Edit Workflow
                            </Button>
                          )}

                          {/* Approval Button - for Accountable when task is Done */}
                          {canApprove(task) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openApprovalDialog(task)}
                              className="text-purple-700 border-purple-300 hover:bg-purple-50"
                            >
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Review & Approve
                            </Button>
                          )}

                          {/* Extension Button - for overdue tasks (admin/accountable, direct extend) */}
                          {canExtend(task) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openExtensionDialog(task)}
                              className="text-orange-700 border-orange-300 hover:bg-orange-50"
                            >
                              <CalendarPlus className="h-4 w-4 mr-1" />
                              Extend Due Date
                            </Button>
                          )}
                          {/* Request Extension - for members without direct-extend power */}
                          {!canExtend(task) && task.responsible?.includes(user?.id) && task.status !== 'Done' && task.extensionRequestStatus !== 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRequestExtensionDialog(task)}
                              className="text-orange-700 border-orange-300 hover:bg-orange-50"
                              data-testid={`request-extension-${task.id}`}
                            >
                              <CalendarPlus className="h-4 w-4 mr-1" />
                              Request Extension
                            </Button>
                          )}
                          {task.extensionRequestStatus === 'pending' && (
                            <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                              Extension requested - awaiting admin review
                            </span>
                          )}

                          {/* Archive/Unarchive Button - admin only */}
                          {user?.role === 'admin' && task.status === 'Done' && !isArchivedForUser(task) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchive(task)}
                              className="text-slate-700 border-slate-300 hover:bg-slate-50"
                              data-testid={`archive-task-${task.id}`}
                            >
                              <Archive className="h-4 w-4 mr-1" />
                              Archive
                            </Button>
                          )}
                          {user?.role === 'admin' && isArchivedForUser(task) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnarchive(task)}
                              className="text-blue-700 border-blue-300 hover:bg-blue-50"
                              data-testid={`unarchive-task-${task.id}`}
                            >
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      ))}

      {Object.keys(groupedTasks).length === 0 && (
        <Card className="border-border/60 bg-white">
          <div className="p-12 text-center">
            <p className="text-muted-foreground">No tasks assigned yet</p>
          </div>
        </Card>
      )}

      {/* Completion Dialog */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Mark Task as Complete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              {member?.username === 'aditya' ? 'Please provide details about the completed work.' : 'Please provide details about the completed work. At least one field is required.'}
            </p>
            
            <div>
              <Label className="text-sm font-semibold">
                What was done? <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={completionData.completionNote}
                onChange={(e) => setCompletionData({ ...completionData, completionNote: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white"
                placeholder="Describe what was accomplished (e.g., 'Created 5 social media posts with graphics')"
              />
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Link to work {member?.username !== 'aditya' && <span className="text-red-500">*</span>}
              </Label>
              <Input
                value={completionData.completionLinks}
                onChange={(e) => setCompletionData({ ...completionData, completionLinks: e.target.value })}
                className="mt-1.5 bg-white"
                placeholder="https://drive.google.com/..., https://figma.com/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Comma-separated if multiple links
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setCompletionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCompletionSubmit}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark as Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Review & Approve Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTask && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-semibold">{selectedTask.title}</p>
                <p className="text-sm text-muted-foreground">{selectedTask.deliverable}</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-semibold">Decision *</Label>
              <div className="flex gap-3 mt-2">
                <Button
                  type="button"
                  variant={approvalData.approvalStatus === 'approved' ? 'default' : 'outline'}
                  className={approvalData.approvalStatus === 'approved' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setApprovalData({ ...approvalData, approvalStatus: 'approved' })}
                >
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button
                  type="button"
                  variant={approvalData.approvalStatus === 'revision_requested' ? 'default' : 'outline'}
                  className={approvalData.approvalStatus === 'revision_requested' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                  onClick={() => setApprovalData({ ...approvalData, approvalStatus: 'revision_requested' })}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Request Revision
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold">
                Feedback / Comments {approvalData.approvalStatus === 'revision_requested' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={approvalData.approvalComment}
                onChange={(e) => setApprovalData({ ...approvalData, approvalComment: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white"
                placeholder="Provide feedback or comments..."
              />
            </div>

            {approvalData.approvalStatus === 'revision_requested' && (
              <div>
                <Label className="text-sm font-semibold">New Due Date (optional)</Label>
                <Input
                  type="date"
                  value={approvalData.newDueDate}
                  onChange={(e) => setApprovalData({ ...approvalData, newDueDate: e.target.value })}
                  className="mt-1.5 bg-white"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setApprovalDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleApprovalSubmit}
                disabled={!approvalData.approvalStatus}
                className="bg-primary hover:bg-primary/90"
              >
                Submit Review
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extension Dialog */}
      <Dialog open={extensionDialogOpen} onOpenChange={setExtensionDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Extend Due Date</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTask && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="font-semibold text-red-800">{selectedTask.title}</p>
                <p className="text-sm text-red-600">
                  Current due date: {selectedTask.dueDate ? format(new Date(selectedTask.dueDate), 'MMM dd, yyyy') : 'Not set'}
                </p>
                <p className="text-xs text-red-500 mt-1">⚠️ This task is overdue</p>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-semibold">New Due Date *</Label>
              <Input
                type="date"
                value={extensionData.newDueDate}
                onChange={(e) => setExtensionData({ ...extensionData, newDueDate: e.target.value })}
                className="mt-1.5 bg-white"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div>
              <Label className="text-sm font-semibold">Reason for Extension *</Label>
              <Textarea
                value={extensionData.extensionReason}
                onChange={(e) => setExtensionData({ ...extensionData, extensionReason: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white"
                placeholder="Explain why the due date needs to be extended..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setExtensionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleExtensionSubmit}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Extend Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Request Extension Dialog (for members without direct-extend power) */}
      <Dialog open={requestExtensionOpen} onOpenChange={setRequestExtensionOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Request Due Date Extension</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTask && (
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="font-semibold text-orange-800">{selectedTask.title}</p>
                <p className="text-sm text-orange-600">
                  Current due date: {selectedTask.dueDate ? format(new Date(selectedTask.dueDate), 'MMM dd, yyyy') : 'Not set'}
                </p>
                <p className="text-xs text-orange-500 mt-1">This will be sent to an admin for review.</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-semibold">Proposed New Due Date *</Label>
              <Input
                type="date"
                value={requestExtensionData.requestedDate}
                onChange={(e) => setRequestExtensionData({ ...requestExtensionData, requestedDate: e.target.value })}
                className="mt-1.5 bg-white"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Label className="text-sm font-semibold">Reason *</Label>
              <Textarea
                value={requestExtensionData.reason}
                onChange={(e) => setRequestExtensionData({ ...requestExtensionData, reason: e.target.value })}
                rows={3}
                className="mt-1.5 bg-white"
                placeholder="Explain why you need more time..."
              />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRequestExtensionOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRequestExtensionSubmit}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <CalendarPlus className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Completion Dialog for Batched Workflow Tasks */}
      <Dialog open={stageCompletionDialogOpen} onOpenChange={setStageCompletionDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedTask?.workflowStages?.[selectedTask?.currentStageIndex]?.name === 'Published' 
                ? 'Publishing Stage' 
                : 'Complete Your Stage'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTask && (
              <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                <p className="font-semibold text-violet-800">{selectedTask.title}</p>
                <p className="text-sm text-violet-600">
                  {selectedTask.postCount} post{selectedTask.postCount > 1 ? 's' : ''} • {selectedTask.accountName}
                </p>
                {selectedTask.workflowStages && (
                  <div className="flex items-center gap-1 mt-2 flex-wrap">
                    {selectedTask.workflowStages.map((stage, idx) => (
                      <div key={stage.name + idx} className="flex items-center">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          stage.status === 'done' ? 'bg-green-200 text-green-800' :
                          stage.status === 'in_progress' ? 'bg-blue-200 text-blue-800 font-bold' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {stage.name}
                        </span>
                        {idx < selectedTask.workflowStages.length - 1 && (
                          <span className="text-gray-400 mx-1">→</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Show client revision history if exists */}
                {selectedTask.clientRevisions?.length > 0 && (
                  <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                    <p className="text-xs font-medium text-amber-700">⚠️ Revision History ({selectedTask.clientRevisions.length})</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Published Stage - Show different UI */}
            {selectedTask?.workflowStages?.[selectedTask?.currentStageIndex]?.name === 'Published' ? (
              <>
                {/* Mode Selection */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={publishedActionMode === 'publish' ? 'default' : 'outline'}
                    className={publishedActionMode === 'publish' ? 'bg-green-600 hover:bg-green-700' : ''}
                    onClick={() => setPublishedActionMode('publish')}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Mark as Published
                  </Button>
                  <Button
                    type="button"
                    variant={publishedActionMode === 'revision' ? 'default' : 'outline'}
                    className={publishedActionMode === 'revision' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                    onClick={() => setPublishedActionMode('revision')}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Client Revision
                  </Button>
                </div>

                {publishedActionMode === 'publish' ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 font-medium">
                      ✅ Mark this content as published. This will complete the task.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 font-medium">
                        🔄 Client has requested changes. Select who should handle the revision.
                      </p>
                    </div>
                    
                    {/* Reset to stage selection */}
                    <div>
                      <Label className="text-sm font-semibold">
                        Reset to Stage <span className="text-red-500">*</span>
                      </Label>
                      <select
                        value={revisionData.resetToStageIndex}
                        onChange={(e) => setRevisionData({ ...revisionData, resetToStageIndex: parseInt(e.target.value) })}
                        className="mt-1.5 w-full px-3 py-2 border rounded-md bg-white"
                      >
                        {selectedTask?.workflowStages?.map((stage, idx) => (
                          <option key={stage.name + idx} value={idx}>
                            {stage.name} {idx === 0 ? '(Start Over)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Assignee override for reset stage */}
                    <div>
                      <Label className="text-sm font-semibold">
                        Assign Revision To
                      </Label>
                      <select
                        value={revisionData.assigneeOverrides?.[revisionData.resetToStageIndex] || selectedTask?.workflowStages?.[revisionData.resetToStageIndex]?.assigneeId || ''}
                        onChange={(e) => setRevisionData({ 
                          ...revisionData, 
                          assigneeOverrides: {
                            ...revisionData.assigneeOverrides,
                            [revisionData.resetToStageIndex]: e.target.value
                          }
                        })}
                        className="mt-1.5 w-full px-3 py-2 border rounded-md bg-white"
                      >
                        {teamMembers.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name} {member.id === selectedTask?.workflowStages?.[revisionData.resetToStageIndex]?.assigneeId ? '(Current)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-semibold">
                        New Due Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="date"
                        value={revisionData.newDueDate}
                        onChange={(e) => setRevisionData({ ...revisionData, newDueDate: e.target.value })}
                        className="mt-1.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">
                        Revision Note
                      </Label>
                      <Textarea
                        value={revisionData.revisionNote}
                        onChange={(e) => setRevisionData({ ...revisionData, revisionNote: e.target.value })}
                        rows={2}
                        className="mt-1.5 bg-white"
                        placeholder="What changes did the client request?"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStageCompletionDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  {publishedActionMode === 'publish' ? (
                    <Button 
                      onClick={handleMarkPublished}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Mark as Published
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleClientRevision}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      disabled={!revisionData.newDueDate}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Request Revision & Reset
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* Regular stage completion UI */
              <>
                <div>
                  <Label className="text-sm font-semibold">
                    What did you complete? <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    value={stageCompletionData.note}
                    onChange={(e) => setStageCompletionData({ ...stageCompletionData, note: e.target.value })}
                    rows={3}
                    className="mt-1.5 bg-white"
                    placeholder="e.g., 'Completed content for all 3 posts - captions and hashtags ready'"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold">
                    Links to your work <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={stageCompletionData.links}
                    onChange={(e) => setStageCompletionData({ ...stageCompletionData, links: e.target.value })}
                    className="mt-1.5 bg-white"
                    placeholder="https://drive.google.com/..., https://docs.google.com/..."
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Google Drive, Figma, Notion links (comma-separated)
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setStageCompletionDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleStageComplete}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Complete Stage & Pass to Next
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Workflow Edit Dialog - For Admins */}
      <Dialog open={workflowEditDialogOpen} onOpenChange={setWorkflowEditDialogOpen}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Edit Workflow Stages</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedTask && (
              <div className="p-3 bg-slate-50 border rounded-lg">
                <p className="font-semibold text-slate-800">{selectedTask.title}</p>
                <p className="text-sm text-slate-500">{selectedTask.accountName}</p>
              </div>
            )}
            
            <div className="space-y-3">
              {workflowEditData.map((stage, idx) => (
                <div key={stage.name + idx} className="flex items-center gap-3 p-3 bg-white border rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-700 font-bold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{stage.name}</p>
                  </div>
                  <div className="flex-1">
                    <select
                      value={stage.assigneeId || ''}
                      onChange={(e) => {
                        const updated = [...workflowEditData];
                        updated[idx].assigneeId = e.target.value;
                        setWorkflowEditData(updated);
                      }}
                      className="w-full px-2 py-1.5 border rounded text-sm"
                    >
                      {teamMembers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="w-32">
                    <Input
                      type="date"
                      value={stage.dueDate || ''}
                      onChange={(e) => {
                        const updated = [...workflowEditData];
                        updated[idx].dueDate = e.target.value;
                        setWorkflowEditData(updated);
                      }}
                      className="text-sm h-8"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setWorkflowEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSaveWorkflow}
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default TeamTasks;
